import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { VALUE_RANGES, CONTRACT_STATUSES, formatValue } from "../data/data";
import SearchBar from "../components/SearchBar";
import Pagination from "../components/Pagination";
import Modal from "../components/Modal";
import { getAllProjects, createProject, updateProject, searchPartners, createPartner } from "../service/rpc";
import { useIsMobile } from "../hooks/useIsMobile";
import { useUser } from "../context/UserContext";
import toast from "react-hot-toast";

const ITEMS_PER_PAGE = 6;

const EMPTY_FORM = { name: "", customerName: "", partner_id: "", budget: "", endDate: "", startDate: "", description: "", status: "Draft" };

const STATUS_MAP = {
  'draft': 'Draft',
  'in_progress': 'Active',
  'done': 'Completed',
  'cancelled': 'Cancelled'
};

const STATUS_REVERSE_MAP = {
  'Draft': 'draft',
  'Active': 'in_progress',
  'Completed': 'done',
  'Cancelled': 'cancelled'
};


const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};


export default function ContractsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [valueRange, setValueRange] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();
  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");

  const [contractsData, setContractsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' | 'edit'
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [totalItems, setTotalItems] = useState(0);
  const [saving, setSaving] = useState(false);
  const { partnerId, loading: userLoading, uid, name: userName } = useUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * ITEMS_PER_PAGE;

        let domain = [];

        if (search.trim()) {
          const q = search.trim();
          const searchDomain = ['|', ['name', 'ilike', q], ['partner_id', 'ilike', q]];
          domain = domain.length > 0 ? ['&', ...domain, ...searchDomain] : searchDomain;
        }

        if (statusFilter !== "All") {
          const technicalStatus = STATUS_REVERSE_MAP[statusFilter];
          if (technicalStatus) {
            const statusDomain = [['state', '=', technicalStatus]];
            domain = domain.length > 0 ? ['&', ...domain, ...statusDomain] : statusDomain;
          }
        }

        const filterdata = VALUE_RANGES.find(vr => vr.label === valueRange);
        if (filterdata && filterdata.min !== 0) {
          const valueDomain = ['&', ['budget', '>=', filterdata.min], ['budget', '<=', filterdata.max]];
          domain = domain.length > 0 ? ['&', ...domain, ...valueDomain] : valueDomain;
        }

        const { projects, count } = await getAllProjects(domain, 100, offset);

        const formatted = projects.map(p => ({
          id: p.id,
          name: p.name || "Unnamed Project",
          client: p.partner_id && p.partner_id.length > 1 ? p.partner_id[1] : "N/A",
          partner_id_raw: p.partner_id,
          status: STATUS_MAP[p.state] || "Draft",
          startDate: p.date_start && typeof p.date_start === 'string' ? p.date_start.split(' ')[0] : (p.date_start || "N/A"),
          endDate: p.date_end && typeof p.date_end === 'string' ? p.date_end.split(' ')[0] : (p.date_end || "N/A"),
          description: stripHtml(p.description) || "No description",
          budget: p.budget || 0,
        }));
        setContractsData(formatted);
        setTotalItems(count);
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };

    if (!userLoading) {
      fetchData();
    }
  }, [search, currentPage, statusFilter, partnerId, uid, userLoading]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setModalMode("create");
    setForm({ ...EMPTY_FORM, customerName: userName || "", partner_id: partnerId || "", endDate: today });
    setEditTarget(null);
    setModalOpen(true);
  };

  const [partnerSuggestions, setPartnerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const fetchPartners = async (q) => {
    try {
      const results = await searchPartners(q);
      setPartnerSuggestions(results);
      setShowSuggestions(true);
    } catch (e) {
      console.error(e);
    }
  };

  const openEdit = (e, contract) => {
    e.stopPropagation();
    setModalMode("edit");
    setForm({
      name: contract.name,
      budget: String(contract.budget || 0),
      startDate: contract.startDate,
      endDate: contract.endDate,
      description: contract.description || "",
      status: contract.status || "Draft",
    });
    setEditTarget(contract);
    setModalOpen(true);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {
        name: form.name,
        partner_id: form.partner_id ? parseInt(form.partner_id) : (partnerId ? parseInt(partnerId) : false),
        date_start: form.startDate || false,
        date_end: form.endDate || false,
        user_id: parseInt(uid),
        description: form.description,
        state: STATUS_REVERSE_MAP[form.status] || 'draft',
        budget: parseFloat(form.budget) || 0,
      };

      if (modalMode === "create") {
        const newId = await createProject(payload);
        setContractsData(prev => [{
          id: newId,
          name: form.name,
          client: userName || "Your Profile",
          partner_id_raw: partnerId ? [partnerId, userName] : null,
          budget: parseFloat(form.budget) || 0,
          status: form.status,
          startDate: form.startDate || "N/A",
          endDate: form.endDate || "N/A",
          description: form.description,
        }, ...prev]);
      } else {
        await updateProject(editTarget.id, payload);
        setContractsData(prev => prev.map(c => c.id === editTarget.id
          ? {
            ...c,
            name: form.name,
            budget: parseFloat(form.budget) || 0,
            status: form.status,
            startDate: form.startDate || c.startDate,
            endDate: form.endDate || c.endDate,
            description: form.description,
          }
          : c
        ));
      }
      setModalOpen(false);
      toast.success(modalMode === "create" ? "Contract created successfully!" : "Contract updated successfully!");
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const pagedData = contractsData;

  return (
    <main className="main">
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalMode === "create" ? "Create Contract" : "Edit Contract"}
        onSave={handleSave}
        saveLabel={modalMode === "create" ? "Create" : "Update"}
        saving={saving}
      >

        <div className="modal-form__group">
          <label className="modal-form__label">Contract Name</label>
          <input className="modal-form__input" name="name" value={form.name} onChange={handleFieldChange} placeholder="e.g. Project Alpha" />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Budget Value (₹)</label>
          <input className="modal-form__input" type="number" name="budget" value={form.budget} onChange={handleFieldChange} placeholder="0" />
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Timeline</label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block' }}>Start Date</label>
              <input className="modal-form__input" type="date" name="startDate" value={form.startDate} onChange={handleFieldChange} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'block' }}>End Date</label>
              <input className="modal-form__input" type="date" name="endDate" value={form.endDate} onChange={handleFieldChange} />
            </div>
          </div>
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Status</label>
          <select className="modal-form__input" name="status" value={form.status} onChange={handleFieldChange}>
            {CONTRACT_STATUSES.filter(s => s !== "All").map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="modal-form__group">
          <label className="modal-form__label">Description</label>
          <textarea
            className="modal-form__input"
            name="description"
            value={form.description}
            onChange={handleFieldChange}
            placeholder="Contract description..."
            rows={6}
            style={{ resize: 'vertical' }}
          />
        </div>
      </Modal>

      <div className="page-header">
        <h1 className="page-header__title">Contracts</h1>
      </div>

      <div className="task-tab-header">
        <div className="page-header__actions">
          <button className="btn-create" onClick={openCreate}>+  New</button>
        </div>
        <SearchBar
          search={search}
          setSearch={setSearch}
          valueRange={valueRange}
          setValueRange={setValueRange}
          valueRanges={VALUE_RANGES}
          placeholder="Search by name or client…"
          style={{ flex: 1, margin: 0 }}
        />

        <div className="view-toggle">
          <button className={`view-toggle__btn ${viewType === 'list' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('list')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            List
          </button>
          <button className={`view-toggle__btn ${viewType === 'grid' ? 'view-toggle__btn--active' : ''}`} onClick={() => setViewType('grid')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
            </svg>
            Kanban
          </button>
        </div>
      </div>

      <div className={`table-card ${loading ? 'table-card--loading' : ''}`}>
        {loading && (
          <div className="list-loading-overlay">
            <div className="loader"></div>
            <p style={{ color: '#64748b', fontWeight: '500', fontFamily: 'Sora' }}>Loading...</p>
          </div>
        )}
        <div className="status-pills">
          {CONTRACT_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`status-pills__pill status-pills__pill--${s.toLowerCase().replace(/\s+/g, '-')} ${statusFilter === s ? "status-pills__pill--selected" : ""}`}
            >
              {s}
            </button>
          ))}
        </div>

        {viewType === "list" ? (
          <table className="table">
            <thead>
              <tr>
                {/* <th className="table__th" >Start Date</th> */}
                <th className="table__th">Project Name</th>
                <th className="table__th" style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedData.length === 0 ? (
                <tr><td className="table__empty" colSpan={3}>No contracts match your filters.</td></tr>
              ) : pagedData.map((c) => (
                <tr key={c.id} className="table__row table__row--clickable" onClick={() => navigate(`/contracts/${c.id}`)}>
                  {/* <td className="table__td">
                    <span className="table__client">
                      {new Date(c.startDate).toLocaleDateString()}
                    </span>
                  </td> */}
                  <td className="table__td"><span className="table__name">{c.name}</span></td>
                  <td className="table__td" style={{ textAlign: 'right' }}>
                    <button className="btn-row-edit" onClick={(e) => openEdit(e, c)} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="task-grid">
            {pagedData.length === 0 ? (
              <div className="table__empty" style={{ padding: '40px' }}>No contracts match your filters.</div>
            ) : pagedData.map((c) => (
              <div key={c.id} className="kanban-card" onClick={() => navigate(`/contracts/${c.id}`)}>
                <div className="kanban-card__top">
                <span className="kanban-card__title">{c.name}</span>
                  <button className="btn-row-edit" onClick={(e) => openEdit(e, c)} title="Edit">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

        )}

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>
    </main>

  );
}
